import { useEffect, useState, useCallback } from 'preact/hooks';
import {
  listDevices,
  linkDevice,
  unlinkDevice,
  startVirtual,
  stopVirtual,
  executeAction,
  type DevicePayload,
  type DevicesListResponse,
  ApiError,
} from '../lib/api';
import { DeviceCard } from './DeviceCard';
import { LinkForm } from './LinkForm';
import { VCForm } from './VCForm';

interface DashboardProps {
  onLogout: () => void;
}

type LoadState = 'loading' | 'ready' | 'error';
type AddMode = 'none' | 'charger' | 'virtual';

export function Dashboard({ onLogout }: DashboardProps) {
  const [devices, setDevices] = useState<DevicePayload[]>([]);
  const [loadState, setLoadState] = useState<LoadState>('loading');
  const [error, setError] = useState<string | null>(null);
  const [actionBusy, setActionBusy] = useState<string | null>(null);
  const [addMode, setAddMode] = useState<AddMode>('none');
  const [toast, setToast] = useState<string | null>(null);

  function showToast(msg: string) {
    setToast(msg);
    setTimeout(() => setToast(null), 4000);
  }

  const refresh = useCallback(async () => {
    setLoadState((prev) => (prev === 'ready' ? prev : 'loading'));
    setError(null);
    try {
      const data: DevicesListResponse = await listDevices();
      setDevices(data.chargers || data.devices || []);
      setLoadState('ready');
    } catch (err) {
      setError(errMessage(err, 'Failed to load devices'));
      setLoadState('error');
    }
  }, []);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  useEffect(() => {
    const interval = setInterval(() => void refresh(), 30000);
    const onFocus = () => void refresh();
    window.addEventListener('focus', onFocus);
    return () => { clearInterval(interval); window.removeEventListener('focus', onFocus); };
  }, [refresh]);

  const handleLink = async (input: { service_code: string; username: string; password: string }) => {
    try {
      const result = await linkDevice(input);
      setAddMode('none');
      showToast(result.message || `Linked ${result.count || 1} charger(s)`);
      setLoadState('loading');
      await refresh();
    } catch (err) {
      const msg = err instanceof ApiError ? `${err.message} (${err.status})` : 'Failed to link charger';
      showToast(msg);
      setAddMode('none');
    }
  };

  const handleVCCreated = () => {
    setAddMode('none');
    showToast('Virtual device created');
    setLoadState('loading');
    void refresh();
  };

  const handleUnlink = async (uuid: string) => {
    if (!confirm('Unlink this device from your account?')) return;
    setActionBusy(uuid);
    try {
      await unlinkDevice(uuid);
      setDevices((prev) => prev.filter((d) => d.uuid !== uuid));
      showToast('Device unlinked');
    } catch (err) {
      showToast(err instanceof Error ? err.message : 'Failed to unlink device');
    } finally {
      setActionBusy(null);
    }
  };

  const handleStart = async (uuid: string, serviceType?: string) => {
    setActionBusy(uuid);
    setError(null);
    try {
      if (serviceType === 'virtual') { await startVirtual(uuid); } else { await executeAction(uuid, 'start'); }
      await refresh();
    } catch (err) { setError(errMessage(err, 'Start failed')); } finally { setActionBusy(null); }
  };

  const handleStop = async (uuid: string, serviceType?: string) => {
    setActionBusy(uuid);
    setError(null);
    try {
      if (serviceType === 'virtual') { await stopVirtual(uuid); } else { await executeAction(uuid, 'stop'); }
      await refresh();
    } catch (err) { setError(errMessage(err, 'Stop failed')); } finally { setActionBusy(null); }
  };

  return (
    <div class="dashboard">
      {toast && <div class="alert alert-info toast" role="status">{toast}</div>}
      <header class="app-header">
        <h1>Devices</h1>
        <div class="header-actions">
          <button class="btn btn-secondary header-btn" onClick={() => void refresh()} disabled={loadState === 'loading'}>
            {loadState === 'loading' ? '…' : 'Refresh'}
          </button>
          <button class="btn btn-secondary header-btn" onClick={onLogout}>Logout</button>
        </div>
      </header>

      {error && <div class="alert alert-error" role="alert">{error}</div>}
      {loadState === 'loading' && <div class="card"><p class="muted">Loading devices…</p></div>}

      {loadState === 'ready' && devices.length === 0 && addMode === 'none' && (
        <div class="card empty-state">
          <p>No devices linked yet.</p>
          <div class="empty-actions">
            <button class="btn" onClick={() => setAddMode('charger')}>+ Link charger</button>
            <button class="btn btn-secondary" onClick={() => setAddMode('virtual')}>+ Create virtual device</button>
          </div>
        </div>
      )}

      {loadState === 'ready' && devices.length > 0 && (
        <div class="device-list">
          {devices.map((device) => (
            <div key={device.uuid} class="device-list-item">
              <DeviceCard
                device={device}
                onStart={(uuid) => handleStart(uuid, device.service_type)}
                onStop={(uuid) => handleStop(uuid, device.service_type)}
                busy={actionBusy === device.uuid}
              />
              <button class="btn btn-secondary device-unlink" onClick={() => void handleUnlink(device.uuid)} disabled={actionBusy === device.uuid}>
                Unlink
              </button>
            </div>
          ))}
        </div>
      )}

      {addMode === 'charger' && (
        <>
          <LinkForm onLink={handleLink} />
          <button class="btn btn-secondary btn-block" onClick={() => setAddMode('none')}>Cancel</button>
        </>
      )}

      {addMode === 'virtual' && (
        <VCForm onCreated={handleVCCreated} onCancel={() => setAddMode('none')} />
      )}

      {loadState === 'ready' && addMode === 'none' && devices.length > 0 && (
        <div class="add-device-row">
          <button class="btn btn-secondary" onClick={() => setAddMode('charger')}>+ Charger</button>
          <button class="btn btn-secondary" onClick={() => setAddMode('virtual')}>+ Virtual device</button>
        </div>
      )}
    </div>
  );
}

function errMessage(err: unknown, fallback: string): string {
  if (err instanceof ApiError) return err.message;
  if (err instanceof Error) return err.message;
  return fallback;
}
